import { useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  Database, 
  Workflow, 
  Table2, 
  Calendar, 
  History, 
  Menu,
  X,
  Cloud,
  Zap,
  Building2,
  Shield,
  Eye,
  Book,
  Bot
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const navigation = [
  { name: "Dashboard", href: "/", icon: LayoutDashboard },
  { name: "Tenants", href: "/tenants", icon: Building2 },
  { name: "Connections", href: "/connections", icon: Database },
  { name: "Table Browser", href: "/tables", icon: Table2 },
  { name: "Pipelines", href: "/pipelines", icon: Workflow },
  { name: "Data Preview", href: "/preview", icon: Eye },
  { name: "CDC", href: "/cdc", icon: Zap },
  { name: "RLS", href: "/rls", icon: Shield },
  { name: "AI Agent", href: "/ai-agent", icon: Bot },
  { name: "Schedules", href: "/schedules", icon: Calendar },
  { name: "History", href: "/history", icon: History },
  { name: "Documentation", href: "/docs", icon: Book },
];

const NavItem = ({ item, onClick }) => {
  const location = useLocation();
  const isActive = location.pathname === item.href || 
    (item.href !== "/" && location.pathname.startsWith(item.href));
  
  return (
    <NavLink
      to={item.href}
      onClick={onClick}
      data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
      className={cn(
        "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200",
        isActive
          ? "bg-primary text-primary-foreground shadow-sm"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
    >
      <item.icon className="w-5 h-5" strokeWidth={1.5} />
      {item.name}
    </NavLink>
  );
};

const Sidebar = ({ onLinkClick }) => (
  <div className="flex flex-col h-full">
    {/* Logo */}
    <div className="px-6 py-6 border-b">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
          <Cloud className="w-6 h-6 text-white" strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="text-lg font-bold tracking-tight">UltiDataLake</h1>
          <p className="text-xs text-muted-foreground">Oracle to BigQuery ETL</p>
        </div>
      </div>
    </div>
    
    {/* Navigation */}
    <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto">
      {navigation.map((item) => (
        <NavItem key={item.name} item={item} onClick={onLinkClick} />
      ))}
    </nav>
    
    {/* Footer */}
    <div className="px-6 py-4 border-t">
      <p className="text-xs text-muted-foreground">
        UltiDataLake v1.0
      </p>
    </div>
  </div>
);

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  
  // Get current page title
  const currentPage = navigation.find(
    item => item.href === location.pathname || 
    (item.href !== "/" && location.pathname.startsWith(item.href))
  );
  const pageTitle = currentPage?.name || "UltiDataLake";

  return (
    <div className="flex h-screen bg-surface">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 bg-white border-r">
        <Sidebar />
      </aside>
      
      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64">
          <Sidebar onLinkClick={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="glass-header px-6 py-4 flex items-center gap-4">
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
          </Sheet>
          <h1 className="text-xl font-semibold tracking-tight">{pageTitle}</h1>
        </header>
        
        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6 lg:p-8">
          <div className="page-enter">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
